import { streamSse } from "./sse";
import dialogStyles from "./dialog.css";
import { loadMessages, saveMessages } from "./storage";
import { themeOverridesCss } from "./theme";
import type {
  ChatMessage,
  EmbedOptions,
  PublicSiteConfig,
} from "./types";

// One dialog instance per page lifecycle. Reopening is a no-op showModal().
let instance: DialogController | null = null;

export function openDialog(opts: EmbedOptions, config: PublicSiteConfig) {
  if (!instance) instance = new DialogController(opts, config);
  instance.open();
}

class DialogController {
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private dialog!: HTMLDialogElement;
  private messagesEl!: HTMLDivElement;
  private inputEl!: HTMLTextAreaElement;
  private formEl!: HTMLFormElement;
  private honeypotEl!: HTMLInputElement;
  private sendBtn!: HTMLButtonElement;
  private startersEl!: HTMLDivElement;
  private messages: ChatMessage[];
  private streaming = false;
  private abort: AbortController | null = null;

  constructor(
    private readonly opts: EmbedOptions,
    private readonly config: PublicSiteConfig,
  ) {
    this.messages = loadMessages(opts.site);

    this.host = document.createElement("div");
    // `font: inherit` lets dialog text pick up the host page's typography
    // through `all: initial` (which would otherwise reset font to UA default).
    this.host.style.cssText = "all: initial; font: inherit; position: fixed; inset: 0; pointer-events: none; z-index: 2147483600;";
    // Smooth-scroll libs (Lenis, Locomotive) hijack wheel/touch at window level.
    // The attribute is Lenis's escape hatch; stopPropagation covers the rest.
    this.host.setAttribute("data-lenis-prevent", "");
    document.body.appendChild(this.host);
    this.shadow = this.host.attachShadow({ mode: "closed" });

    this.render();
  }

  open() {
    if (!this.dialog.open) this.dialog.showModal();
    this.inputEl.focus();
  }

  private render() {
    const style = document.createElement("style");
    // Theme overrides come AFTER the main stylesheet so DB-configured colors
    // win over the :host fallbacks declared in dialog.css.
    style.textContent = dialogStyles + themeOverridesCss(this.config.themeOverrides);
    this.shadow.appendChild(style);

    this.dialog = document.createElement("dialog");
    this.dialog.className = "dialog";
    this.dialog.setAttribute("aria-label", this.config.dialogTitle);
    // Native light-dismiss: Esc + click-outside both close. Replaces a manual
    // backdrop-click handler. Safari support is still landing (Interop 2026);
    // older Safari falls back to Esc-only, which is acceptable here.
    this.dialog.setAttribute("closedby", "any");
    this.dialog.style.pointerEvents = "auto";
    this.dialog.innerHTML = `
      <header class="header">
        <span class="title">${escapeHtml(this.config.dialogTitle)}</span>
        <button type="button" class="close" aria-label="Close chat">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>
          </svg>
        </button>
      </header>
      <div class="messages" role="log" aria-live="polite"></div>
      <div class="starters"></div>
      <form class="form">
        <input type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
        <textarea class="input" placeholder="Type your question..." rows="1"></textarea>
        <button type="submit" class="send" aria-label="Send" disabled>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
        </button>
      </form>
    `;
    this.shadow.appendChild(this.dialog);

    this.messagesEl = this.dialog.querySelector(".messages") as HTMLDivElement;
    this.startersEl = this.dialog.querySelector(".starters") as HTMLDivElement;
    this.inputEl = this.dialog.querySelector(".input") as HTMLTextAreaElement;
    this.formEl = this.dialog.querySelector(".form") as HTMLFormElement;
    this.honeypotEl = this.dialog.querySelector('input[name="website"]') as HTMLInputElement;
    this.sendBtn = this.dialog.querySelector(".send") as HTMLButtonElement;

    // Stop wheel/touch from reaching window-level smooth-scroll listeners
    // (Lenis et al.) so native scroll on .messages works.
    const stop = (e: Event) => e.stopPropagation();
    this.dialog.addEventListener("wheel", stop, { passive: true });
    this.dialog.addEventListener("touchmove", stop, { passive: true });

    this.dialog.querySelector(".close")!.addEventListener("click", () => {
      this.dialog.close();
    });
    // Fallback for browsers without `closedby` support (older Safari).
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });
    this.inputEl.addEventListener("input", () => {
      this.sendBtn.disabled = !this.inputEl.value.trim() || this.streaming;
    });
    this.inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.formEl.requestSubmit();
      }
    });
    this.formEl.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = this.inputEl.value.trim();
      if (!text || this.streaming) return;
      this.inputEl.value = "";
      this.sendBtn.disabled = true;
      void this.send(text);
    });

    this.renderMessages();
    this.renderStarters();
  }

  private renderStarters() {
    this.startersEl.innerHTML = "";
    if (this.messages.length > 0 || this.config.starterPrompts.length === 0) {
      this.startersEl.style.display = "none";
      return;
    }
    this.startersEl.style.display = "flex";
    for (const starter of this.config.starterPrompts) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "starter";
      btn.textContent = starter.q;
      btn.addEventListener("click", () => {
        this.startersEl.style.display = "none";
        // Canned answer: render locally so the chip feels instant and we don't
        // spend a /v1/chat turn (or a model call) on a zero-cost response.
        if (starter.a) this.sendCanned(starter.q, starter.a);
        else void this.send(starter.q);
      });
      this.startersEl.appendChild(btn);
    }
  }

  private sendCanned(question: string, answer: string) {
    this.messages.push({ role: "user", content: question });
    this.messages.push({ role: "assistant", content: answer });
    if (this.messagesEl.firstChild instanceof HTMLElement &&
        this.messagesEl.firstChild.classList.contains("empty")) {
      this.messagesEl.innerHTML = "";
    }
    this.messagesEl.appendChild(this.messageElement({ role: "user", content: question }));
    this.messagesEl.appendChild(this.messageElement({ role: "assistant", content: answer }));
    saveMessages(this.opts.site, this.messages);
    this.scrollToBottom();
  }

  private renderMessages() {
    this.messagesEl.innerHTML = "";
    if (this.messages.length === 0) {
      const empty = document.createElement("div");
      empty.className = "empty";
      empty.textContent = "Hi! Ask me anything.";
      this.messagesEl.appendChild(empty);
      return;
    }
    for (const m of this.messages) {
      this.messagesEl.appendChild(this.messageElement(m));
    }
    this.scrollToBottom();
  }

  private messageElement(m: ChatMessage): HTMLDivElement {
    const el = document.createElement("div");
    el.className = `message message-${m.role}`;
    el.textContent = m.content;
    return el;
  }

  private scrollToBottom() {
    this.messagesEl.scrollTo({
      top: this.messagesEl.scrollHeight,
      behavior: "smooth",
    });
  }

  private async send(text: string) {
    this.messages.push({ role: "user", content: text });
    this.renderStarters();
    if (this.messagesEl.firstChild instanceof HTMLElement &&
        this.messagesEl.firstChild.classList.contains("empty")) {
      this.messagesEl.innerHTML = "";
    }
    this.messagesEl.appendChild(this.messageElement({ role: "user", content: text }));

    const assistantMsg: ChatMessage = { role: "assistant", content: "" };
    this.messages.push(assistantMsg);
    const assistantEl = this.messageElement(assistantMsg);
    this.messagesEl.appendChild(assistantEl);
    this.scrollToBottom();

    this.streaming = true;
    this.sendBtn.disabled = true;
    this.abort = new AbortController();

    try {
      const res = await fetch(`${this.opts.apiBase}/v1/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site: this.opts.site,
          messages: this.messages.slice(0, -1), // strip empty assistant placeholder
          // Per-request page context. Read at send time so SPA navigation
          // between turns is reflected. Worker length-caps both.
          pageUrl: location.href,
          pageTitle: document.title,
          website: this.honeypotEl.value,
        }),
        signal: this.abort.signal,
      });

      if (!res.ok) {
        const reason = res.status === 429 ? "rate_limited" : "error";
        throw new Error(reason);
      }

      for await (const ev of streamSse(res, this.abort.signal)) {
        if (ev.event === "done") break;
        if (ev.event === "error") {
          throw new Error("stream_error");
        }
        try {
          const parsed = JSON.parse(ev.data) as { text?: string };
          if (parsed.text) {
            assistantMsg.content += parsed.text;
            assistantEl.textContent = assistantMsg.content;
            this.scrollToBottom();
          }
        } catch {
          /* ignore malformed chunk */
        }
      }
    } catch (err) {
      const errorEl = document.createElement("div");
      errorEl.className = "message message-error";
      errorEl.textContent = this.errorText(err);
      this.messagesEl.appendChild(errorEl);
      // Roll back the placeholder so retry doesn't include an empty assistant turn.
      this.messages.pop();
      if (assistantEl.parentNode) assistantEl.remove();
    } finally {
      this.streaming = false;
      this.sendBtn.disabled = !this.inputEl.value.trim();
      saveMessages(this.opts.site, this.messages);
      this.scrollToBottom();
    }
  }

  private errorText(err: unknown): string {
    const phone = this.config.fallbackPhone;
    const base = err instanceof Error && err.message === "rate_limited"
      ? "You've reached the message limit for now."
      : "Something went wrong.";
    if (phone) return `${base} You can reach a human at ${phone}.`;
    if (this.config.fallbackUrl) return `${base} You can reach a human via ${this.config.fallbackUrl}.`;
    return `${base} Please try again later.`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
